import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAdminSettingsBatch } from "@/hooks/useAdminSettingsBatch";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export const GTM_CONTAINER_ID_KEY = "gtm_container_id";
export const GTM_ENABLED_KEY = "gtm_enabled";

const isValidGtmId = (id: string) => /^GTM-[A-Z0-9]+$/i.test(id);

/**
 * Reads the admin-configured Google Tag Manager container ID + enabled flag
 * from `admin_settings` (Theme Options → Analytics) and injects the GTM loader
 * plus the <noscript> iframe fallback at runtime. Loads on every route —
 * marketing, portal and admin — and pushes a `page_view` to the dataLayer on
 * client-side navigations, since GTM only sees the initial load by itself.
 */
export function useGoogleTagManagerSettings() {
  return useAdminSettingsBatch<{ containerId: string; enabled: boolean }>((map) => ({
    containerId: (map[GTM_CONTAINER_ID_KEY] ?? "").trim(),
    enabled: (map[GTM_ENABLED_KEY] ?? "") === "true",
  }));
}

export function useGoogleTagManager() {
  const { data } = useGoogleTagManagerSettings();
  const location = useLocation();

  const containerId = data?.containerId ?? "";
  const active = Boolean(data?.enabled && containerId && isValidGtmId(containerId));

  useEffect(() => {
    if (!active) return;
    if (document.getElementById("gtm-loader")) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    const script = document.createElement("script");
    script.id = "gtm-loader";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    document.head.appendChild(script);

    // <noscript> fallback, kept as the first child of <body> per Google's snippet.
    const noscript = document.createElement("noscript");
    noscript.id = "gtm-noscript";
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    iframe.title = "Google Tag Manager";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }, [active, containerId]);

  useEffect(() => {
    if (!active) return;
    if (!Array.isArray(window.dataLayer)) return;

    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [active, location.pathname, location.search]);
}
